import {useEffect, useEffectEvent} from 'react'
import useIsMounted from '@github-ui/use-is-mounted'
import {getSession} from '@github-ui/alive'
import {connectAliveSubscription} from '@github-ui/alive/connect-alive-subscription'
import {useTestSubscribeToAlive} from './TestAliveSubscription'

/**
 * Subscribe to an alive channel with a signed channel name. Event data
 * will be passed to the callback.
 *
 * This is a thin wrapper around {@link useAliveChannels} for the common
 * single-channel case.
 * @param signedChannel the signed channel name, provided from the server
 * @param callback a callback to receive events from the alive channel. It does not need to be memoized — the latest callback is always used, and changing it does not cause a resubscribe.
 */
export function useAlive<T>(signedChannel: string | null | undefined, callback: (data: T) => unknown) {
  useAliveChannels([signedChannel], callback)
}

/**
 * Subscribe to a variable number of alive channels with a single hook, all
 * sharing the same callback. Falsy (null/undefined) channels are ignored.
 *
 * Prefer this over calling {@link useAlive} once per field when the channels
 * come from a fixed-shape payload object: pass `Object.values(channels)` so the
 * subscription list is derived from the data and can never drift out of sync
 * with the channel type.
 *
 * @param signedChannels signed channel names, provided from the server
 * @param callback a callback to receive events from any of the channels. It does not need to be memoized — the latest callback is always used, and changing it does not cause a resubscribe.
 */
export function useAliveChannels<T>(signedChannels: Array<string | null | undefined>, callback: (data: T) => unknown) {
  const isMounted = useIsMounted()
  const testSubscribeToAlive = useTestSubscribeToAlive()

  const channels = signedChannels.filter((channel): channel is string => Boolean(channel))
  // Cheap, stable change-trigger so the effect re-subscribes only when the ordered
  // list of channels changes (the array identity differs on every render). Because
  // the key is order- and duplicate-sensitive, reordering the channels also
  // triggers a re-subscribe. This is only a *signal* — the effect reads the real
  // channel strings via getLatestChannels — so a delimiter collision here could at
  // worst skip a re-subscribe, never subscribe to a wrong channel. Signed channel
  // names never contain newlines.
  const channelsKey = channels.join('\n')
  // Read the latest channels inside the effect as a non-reactive Effect Event:
  // channelsKey decides *when* we re-subscribe, while this gives the effect the
  // real channel strings without round-tripping them through a delimiter that a
  // channel name could itself contain.
  const getLatestChannels = useEffectEvent(() => channels)

  // Always invoke the latest callback without tearing down and rebuilding every
  // subscription when it changes. Subscriptions call this stable `dispatch`
  // Effect Event, so re-subscription is driven solely by the ordered list of channels.
  const dispatch = useEffectEvent((data: T) => callback(data))

  useEffect(() => {
    const subscribedChannels = getLatestChannels()
    const unsubscribers: Array<() => void> = []
    let closed = false

    async function subscribeToAlive() {
      if (subscribedChannels.length === 0) return

      if (typeof testSubscribeToAlive === 'function') {
        for (const channel of subscribedChannels) {
          const subs = await testSubscribeToAlive(channel, (data: unknown) => dispatch(data as T))
          if (closed) {
            subs?.unsubscribe()
            return
          }
          if (subs?.unsubscribe) unsubscribers.push(subs.unsubscribe)
        }
        return
      }

      try {
        const aliveSession = await getSession()
        if (closed) {
          // Possible we unsubscribed before the session returned
          // this is fine, we just don't subscribe to the channels on the alive side
          return
        }
        for (const channel of subscribedChannels) {
          // Subscribe per-channel so one bad channel (e.g. an invalid signed name
          // that makes connectAliveSubscription throw) can't prevent subscribing
          // to the remaining channels.
          try {
            const resp = connectAliveSubscription(aliveSession, channel, (data: T) => dispatch(data))
            if (resp?.unsubscribe) {
              if (closed || !isMounted()) {
                resp.unsubscribe()
              } else {
                unsubscribers.push(resp.unsubscribe)
              }
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e)
          }
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }

    subscribeToAlive()

    return () => {
      closed = true
      for (const unsubscribe of unsubscribers) unsubscribe()
    }
  }, [channelsKey, isMounted, testSubscribeToAlive])
}
